// ─────────────────────────────────────────────────────────────────────────────
// Webhooks do helpdesk (n8n).
//
// É por aqui que a formalização sai do Orion: o texto do atendimento vai para o
// n8n, que devolve o ticket formalizado e a classificação sugerida pela IA.
// ─────────────────────────────────────────────────────────────────────────────

import { WEBHOOKS, TEAMS_COMERCIAL_WEBHOOK } from '../config/ambiente';
import { numeroDaResposta } from './novoTicketService';
import { gerarNomeImagem } from './imagemAnexo';

/**
 * Chama a webhook de formalização.
 *
 * Com `audio` (um Blob), envia multipart/form-data: o binário no campo "audio"
 * e o resto como texto (objetos e arrays viram JSON string). Sem áudio, envia
 * JSON puro, como sempre foi.
 *
 * A tag `tem_audio` deve vir dentro do próprio payload (true/false).
 */
export async function chamarWebhookHelpdesk(payload, url = WEBHOOKS.helpdesk, audio = null, nomeAudio = 'gravacao.webm') {
  let res;

  if (audio) {
    const fd = new FormData();
    Object.entries(payload).forEach(([k, v]) => {
      if (v === null || v === undefined) return;
      fd.append(k, (typeof v === 'object') ? JSON.stringify(v) : String(v));
    });
    // O navegador define o Content-Type (com o boundary) sozinho — definir à
    // mão quebra o multipart.
    fd.append('audio', audio, nomeAudio);
    res = await fetch(url, { method: 'POST', body: fd });
  } else {
    res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  }

  if (!res.ok) {
    let msg = `Erro ${res.status}`;
    try { const e = await res.json(); msg = e.message || e.error || msg; } catch (err) {}
    throw new Error(msg);
  }
  return await res.json();
}

/**
 * Formaliza o texto do ticket NOVO criado durante um atendimento.
 *
 * Webhook dedicada (`helpdesk-v3`): o fluxo é outro — não é mais uma interação
 * num atendimento em curso, é a abertura de um caso novo, e o prompt de lá
 * escreve nesse tom.
 *
 * São DUAS chamadas, como no 1.4.1: a formalização e, em seguida, a
 * classificação (`/webhook/cat`) com o mesmo payload mais o `texto_final`. A
 * segunda só sugere — quem aplica é o operador.
 *
 * Devolve `{ texto, assunto, sugestao }`. Uma falha na classificação não
 * derruba a formalização: o texto volta mesmo assim, sem sugestão.
 */
export async function formalizarNovoTicket({ anotacoes, ticketOrigem, solicitante, operador, audio = null }) {
  // As oito chaves do 1.4.1, nesta ordem. `numero_ticket` é o ticket de ORIGEM
  // como texto e `tipo_atendimento` é fixo em "Novo ticket" — é por eles que o
  // fluxo distingue esta formalização da do atendimento em curso.
  //
  // Com áudio, vai como no atendimento: multipart com o binário em "audio" e
  // `tem_audio: true` — o fluxo transcreve e formaliza junto com o texto.
  const dados = await chamarWebhookHelpdesk({
    numero_ticket: String(ticketOrigem || ''),
    solicitante,
    tipo_atendimento: 'Novo ticket',
    anotacoes,
    operador_nome: operador?.nome || '',
    operador_email: operador?.email || '',
    novo_ticket: true,
    ticket_origem: parseInt(ticketOrigem, 10) || null,
    tem_audio: !!audio,
  }, WEBHOOKS.helpdeskV3, audio ? audio.blob : null, audio?.nome);

  const r = Array.isArray(dados) ? dados[0] : dados;
  const bruto = r?.ticket_texto || r?.texto_final || r?.ticket || r?.texto || r?.text || r?.output
    || (typeof r === 'string' ? r : '');
  const texto = String(bruto).replace(/<br\s*\/?>/gi, '\n').trim();

  let assunto = '';
  let sugestao = null;
  try {
    const resp = await chamarWebhookHelpdesk({
      numero_ticket: String(ticketOrigem || ''),
      solicitante,
      tipo_atendimento: 'Novo ticket',
      anotacoes,
      operador_nome: operador?.nome || '',
      operador_email: operador?.email || '',
      novo_ticket: true,
      ticket_origem: parseInt(ticketOrigem, 10) || null,
      texto_final: texto || anotacoes,
    }, WEBHOOKS.classificacao);

    const c = Array.isArray(resp) ? resp[0] : resp;
    assunto = c?.assunto || c?.subject || '';
    if (c?.classificacao || c?.ocorrencia || c?.criticidade) {
      sugestao = {
        classificacao: c.classificacao || null,
        ocorrencia: c.ocorrencia || null,
        criticidade: c.criticidade || null,
        codigoClassificacao: c.codigo_classificacao || c.codigoClassificacao || null,
      };
    } else {
      sugestao = c?.classificacoes || c?.classifications || null;
    }
  } catch (err) {
    // A sugestão é acessória: sem ela o operador classifica à mão.
    console.warn('[Orion] Classificação do ticket novo:', err.message);
  }

  return { texto, assunto, sugestao };
}

/**
 * Cria um ticket no MySuite (via n8n) — o mesmo fluxo do "em massa".
 * Devolve `{ numero }`; `numero` pode vir null se o fluxo não o informou, e
 * nesse caso o ticket costuma ter sido criado mesmo assim.
 */
export async function inserirTicketNovo(payload) {
  const res = await fetch(WEBHOOKS.inserirTicket, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`O fluxo de criação respondeu ${res.status}.`);

  let numero = null;
  try {
    numero = numeroDaResposta(await res.json());
  } catch (e) {
    // Resposta sem JSON: quem chamou avisa que o número não veio.
  }
  return { numero };
}

/** Classificação sugerida pela IA para um texto de atendimento. */
export function classificarAtendimento(payload) {
  return chamarWebhookHelpdesk(payload, WEBHOOKS.classificacao);
}

// ── Envio do ticket (OrionAtualiza / OrionContatoSemSucesso) ─────────────────
//
// SEMPRE multipart/form-data, com ou sem imagem — é o que o fluxo do n8n espera
// desde o 1.4.1. Mandar JSON aqui faz o fluxo cair no caminho antigo e o ticket
// chega sem os prints.
//
// O formato é o do 1.4.1, campo por campo:
//   · cada chave do payload como texto (null/undefined viram string vazia);
//   · para cada imagem: `imagem_N` (binário), `legenda_N` e `nome_imagem_N`;
//   · `total_imagens` com a quantidade, sempre — inclusive zero.

/** Converte um data URL em Blob binário. O mime sai do próprio data URL. */
function dataUrlParaBlob(dataUrl) {
  const partes = String(dataUrl || '').split(',');
  const mime = (partes[0].match(/data:([^;]+)/) || [])[1] || 'image/png';
  const bin = atob(partes[1] || '');
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

/**
 * O multipart do envio do ticket, exatamente como o 1.4.1 monta.
 * Exportado para poder ser conferido em teste sem subir a tela.
 */
export function montarFormDataEnvioTicket(payloadObj, imagensArr) {
  const fd = new FormData();
  Object.entries(payloadObj || {}).forEach(([chave, valor]) => {
    fd.append(chave, valor == null ? '' : String(valor));
  });
  const arr = imagensArr || [];
  arr.forEach((img, i) => {
    const nomeArquivo = gerarNomeImagem(img, i);
    fd.append(`imagem_${i}`, dataUrlParaBlob(img.dataUrl), nomeArquivo);
    fd.append(`legenda_${i}`, img.legenda || '');
    // Também como texto: o fluxo grava o nome sem ter de ler o cabeçalho do
    // multipart.
    fd.append(`nome_imagem_${i}`, nomeArquivo);
  });
  fd.append('total_imagens', String(arr.length));
  return fd;
}

/**
 * Envia o ticket para o n8n (OrionAtualiza, ou OrionContatoSemSucesso).
 *
 * Sem `Content-Type` à mão: o navegador escreve o boundary do multipart. E sem
 * ler a resposta como JSON — o fluxo responde vazio, e o 1.4.1 só confere o
 * status.
 */
async function enviarTicket(payload, imagens, url) {
  const res = await fetch(url, {
    method: 'POST',
    body: montarFormDataEnvioTicket(payload, imagens),
  });
  if (!res.ok) throw new Error(`Falha no webhook: ${res.status}`);
  return res;
}

/** Atualiza um ticket existente no MySuite (via n8n), com os prints anexados. */
export function atualizarTicket(payload, imagens = []) {
  return enviarTicket(payload, imagens, WEBHOOKS.atualizarTicket);
}

/** Registra tentativa de contato sem sucesso, com a nova previsão. */
export function registrarContatoSemSucesso(payload, imagens = []) {
  return enviarTicket(payload, imagens, WEBHOOKS.contatoSemSucesso);
}

/** Cria um ticket novo no MySuite (via n8n). */
export function inserirTicket(payload) {
  return chamarWebhookHelpdesk(payload, WEBHOOKS.inserirTicket);
}

/**
 * Avisa o n8n quais tickets o operador tem em atendimento AGORA — sem o que ele
 * acabou de atuar.
 *
 * Não é "assumir o ticket": é o retrato da fila do operador depois de cada ação
 * (envio, ticket novo, criação em massa). É assim no 1.4.1, e o fluxo de lá
 * espera exatamente estas seis chaves.
 *
 * `solicitante` só vai quando for NOME. Vindo só número, é código de usuário —
 * e o fluxo não saberia o que fazer com ele, então segue em branco.
 *
 * Dispara e esquece: uma falha aqui não pode derrubar a ação que acabou de dar
 * certo.
 */
export function notificarEmAtendimento({
  ticketAtuado, tickets, operadorNome, operadorEmail, codigoOperador, solicitante,
}) {
  const atuadoNum = ticketAtuado != null ? Number(ticketAtuado) : null;
  const solicRaw = String(solicitante || '').trim();

  const payload = {
    operadorNome: operadorNome || '',
    operadorEmail: operadorEmail || '',
    codigoOperador: codigoOperador ?? null,
    ticketAtuado: atuadoNum,
    solicitante: /[a-zA-ZÀ-ÿ]/.test(solicRaw) ? solicRaw : '',
    tickets: (tickets || []).filter(c => Number(c) !== atuadoNum),
  };

  return fetch(WEBHOOKS.emAtendimento, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }).catch(err => console.warn('[Orion] Falha ao notificar em-atendimento:', err.message));
}

/**
 * Envia o Relatório de Atendimento já montado.
 *
 * O PDF é gerado no cliente (services/documentoPdf.js) e sobe como binário,
 * exatamente como no 1.4.1 — o n8n recebe o arquivo pronto e o arquiva. Foi por
 * isso que o documento voltou a carregar as imagens: elas são desenhadas aqui,
 * na ordem do texto, e não existiriam num payload JSON.
 */
export async function enviarDocumento(form) {
  // Sem headers: o navegador define o Content-Type com o boundary do multipart.
  const res = await fetch(WEBHOOKS.gerarDocumento, { method: 'POST', body: form });
  if (!res.ok) throw new Error(`O n8n respondeu ${res.status} ao receber o documento.`);
  return res;
}

/**
 * Avisa o canal do Comercial no Teams (via Power Automate).
 * Vai direto, sem passar pelo n8n. Dispara e esquece: o aviso é cortesia, e a
 * formalização não pode travar se o Teams estiver fora do ar.
 *
 * Sem REACT_APP_WEBHOOK_TEAMS_COMERCIAL configurada, não envia nada. A URL
 * carrega a assinatura do fluxo (`sig=`), que é a credencial dele — por isso
 * não tem valor padrão no código (ver config/ambiente.js).
 */
export function avisarComercialNoTeams(tickets) {
  const lista = (Array.isArray(tickets) ? tickets : [tickets])
    .filter(t => t != null && t !== '');
  if (!lista.length) return Promise.resolve();

  if (!TEAMS_COMERCIAL_WEBHOOK) {
    console.warn('[Orion] Aviso ao Comercial não enviado: webhook do Teams não configurado.');
    return Promise.resolve();
  }

  // O corpo é um AdaptiveCard, como no 1.4.1: o Power Automate publica o card
  // no canal tal como ele chega. Um `{ tickets: [...] }` não vira mensagem
  // nenhuma do outro lado.
  const texto = lista.length > 1
    ? `Encaminhar ao setor comercial os Tickets: **${lista.join('**, **')}**`
    : `Encaminhar ao setor comercial o Ticket: **${lista[0]}**`;

  const card = {
    type: 'message',
    attachments: [{
      contentType: 'application/vnd.microsoft.card.adaptive',
      content: {
        type: 'AdaptiveCard',
        $schema: 'http://adaptivecards.io/schemas/adaptive-card.json',
        version: '1.4',
        body: [
          {
            type: 'TextBlock',
            text: '📣 Ticket gerado para o Setor Comercial',
            size: 'large', weight: 'bolder', color: 'accent',
          },
          {
            type: 'TextBlock',
            text: texto,
            size: 'medium', weight: 'bolder', spacing: 'medium', wrap: true,
          },
        ],
      },
    }],
  };

  try {
    return fetch(TEAMS_COMERCIAL_WEBHOOK, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(card),
    })
      .then(res => { if (!res.ok) console.warn('[Orion] Teams (comercial) retornou', res.status); })
      .catch(err => console.warn('[Orion] Falha ao avisar o comercial no Teams:', err.message));
  } catch (err) {
    console.warn('[Orion] Aviso ao Comercial no Teams:', err.message);
    return Promise.resolve();
  }
}
